import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { areaPlatesAPI, type AreaPlate, type AreaPlateMode } from '../../utils/api';
import {
  getAreaPlateMatchValue,
  getAreaPlateModeLabel,
  getAreaPlateModeOptions,
  isBroadMode,
  normalizePlateInput,
  parsePlate,
} from '../../utils/areaPlates';
import '../../pages/Area/Area.css';
import './AreaPlatesModal.css';

interface AreaPlatesModalProps {
  areaId: number;
  areaName: string;
  onClose: () => void;
}

const PLATE_EXAMPLE = 'A777AA77';

/**
 * Белый список номеров участка: по нему внешний API открывает шлагбаумы.
 * mode задаёт, по какой части номера сравнивать (ядро / точный / регион / цифры / буквы).
 */
const AreaPlatesModal: React.FC<AreaPlatesModalProps> = ({ areaId, areaName, onClose }) => {
  const [items, setItems] = useState<AreaPlate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [plateInput, setPlateInput] = useState('');
  const [mode, setMode] = useState<AreaPlateMode>('core');
  const [adding, setAdding] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);

  const parsed = useMemo(() => parsePlate(plateInput), [plateInput]);
  const modeOptions = useMemo(() => getAreaPlateModeOptions(parsed), [parsed]);
  const selectedOption = modeOptions.find((o) => o.value === mode) ?? modeOptions[0];
  // Пустой ввод — ещё не ошибка, подсказываем только когда что-то введено
  const plateInvalid = plateInput.trim() !== '' && parsed === null;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await areaPlatesAPI.getByArea(areaId);
      setItems(res.success ? res.items ?? [] : []);
      if (!res.success) {
        setLoadError(res.message || 'Не удалось загрузить номера участка');
      }
    } catch (e: any) {
      setItems([]);
      setLoadError(e?.message || 'Не удалось загрузить номера участка');
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleAdd = async () => {
    setFormError(null);
    setSuccessMessage(null);

    const normalized = normalizePlateInput(plateInput);
    if (!parsed) {
      setFormError(`Не похоже на номер РФ. Пример: ${PLATE_EXAMPLE}`);
      return;
    }

    try {
      setAdding(true);
      const res = await areaPlatesAPI.add(areaId, normalized, mode);
      if (!res.success) {
        setFormError(res.message || 'Не удалось добавить номер');
        return;
      }
      setSuccessMessage(`Добавлено: ${normalized} — ${selectedOption.label.toLowerCase()}`);
      setPlateInput('');
      setMode('core');
      await load();
    } catch (e: any) {
      setFormError(e?.message || 'Не удалось добавить номер');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (item: AreaPlate) => {
    if (item.id == null) return;
    if (!window.confirm(`Удалить правило для номера ${item.plate}?`)) return;

    setFormError(null);
    setSuccessMessage(null);
    try {
      setDeletingId(item.id);
      const res = await areaPlatesAPI.remove(areaId, item.id);
      if (!res.success) {
        setFormError(res.message || 'Не удалось удалить номер');
        return;
      }
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e: any) {
      setFormError(e?.message || 'Не удалось удалить номер');
    } finally {
      setDeletingId(null);
    }
  };

  const hasBroadRule = items.some((i) => isBroadMode(String(i.mode)));

  return (
    <div className="area-modal" role="dialog" aria-modal="true">
      <div className="area-modal__backdrop" onClick={onClose} />
      <div className="area-modal__dialog area-plates__dialog">
        <header className="area-modal__header">
          <h2 className="area-modal__title">База номеров · {areaName}</h2>
          <span className="area-plates__count">Номеров в базе: {items.length}</span>
          <button className="area-modal__close" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </header>

        <div className="area-plates__body">
          <section className="area-plates__form">
            <div className="area-plates__form-title">Добавить номер</div>
            <div className="area-plates__form-row">
              <div className="area-plates__form-field">
                <input
                  type="text"
                  className={
                    'area-modal__input area-plates__plate-input' +
                    (plateInvalid ? ' area-plates__plate-input--error' : '')
                  }
                  value={plateInput}
                  onChange={(e) => {
                    setPlateInput(e.target.value);
                    setFormError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && parsed && !adding) void handleAdd();
                  }}
                  placeholder={PLATE_EXAMPLE}
                  aria-label="Номер автомобиля"
                  aria-invalid={plateInvalid}
                  disabled={adding}
                />
                <span className="area-plates__hint">можно кириллицей, с пробелами</span>
              </div>

              <div className="area-plates__form-field">
                <select
                  className="area-modal__input area-modal__input--select"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as AreaPlateMode)}
                  aria-label="Как сравнивать номер"
                  disabled={adding}
                >
                  {modeOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                      {o.broad ? ' !' : ''}
                    </option>
                  ))}
                </select>
                <span className="area-plates__hint">
                  {parsed
                    ? `сравнение: ${selectedOption.matchValue}`
                    : 'подписи регионов/цифр/букв появятся после ввода номера'}
                </span>
              </div>

              <button
                type="button"
                className="area-modal__submit area-plates__add-btn"
                onClick={() => void handleAdd()}
                disabled={adding || !parsed}
              >
                {adding ? 'Добавляем…' : 'Добавить'}
              </button>
            </div>

            {plateInvalid && !formError && (
              <div className="area-plates__message area-plates__message--error">
                Не похоже на номер РФ. Пример: {PLATE_EXAMPLE}
              </div>
            )}
            {formError && (
              <div className="area-plates__message area-plates__message--error">{formError}</div>
            )}
            {successMessage && (
              <div className="area-plates__message area-plates__message--success">{successMessage}</div>
            )}
            {selectedOption.broad && parsed && (
              <div className="area-plates__message area-plates__message--warning">
                Это правило пускает много машин: {selectedOption.label.toLowerCase()}.
              </div>
            )}
          </section>

          <section className="area-plates__list">
            {loading ? (
              <div className="ui-loading">Загрузка номеров…</div>
            ) : loadError ? (
              <div className="area-plates__message area-plates__message--error">{loadError}</div>
            ) : items.length === 0 ? (
              <div className="ui-empty">
                <span className="ui-empty__title">Пока ни одного номера</span>
                <span>Добавьте номер машины — шлагбаумы этого участка будут открываться ей.</span>
              </div>
            ) : (
              <>
                <table className="ui-table area-plates__table">
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Как сравнивается</th>
                      <th>Значение</th>
                      <th aria-label="Действия" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const broad = isBroadMode(String(item.mode));
                      return (
                        <tr key={item.id ?? `${item.plate}-${idx}`}>
                          <td>
                            <span className="area-plates__plate">{item.plate}</span>
                          </td>
                          <td className="area-plates__mode">
                            {broad && (
                              <span className="area-plates__broad-mark" title="Правило пускает много машин">
                                !
                              </span>
                            )}
                            {getAreaPlateModeLabel(String(item.mode), item.plate)}
                          </td>
                          <td>
                            <span className="area-plates__value">
                              {getAreaPlateMatchValue(String(item.mode), item.plate, item.value)}
                            </span>
                          </td>
                          <td className="area-plates__actions">
                            <button
                              type="button"
                              className="ui-btn ui-btn--outline ui-btn--sm"
                              onClick={() => void handleDelete(item)}
                              disabled={item.id == null || deletingId === item.id}
                            >
                              {deletingId === item.id ? 'Удаляем…' : 'Удалить'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {hasBroadRule && (
                  <div className="area-plates__legend">! — правило пускает много машин</div>
                )}
              </>
            )}
          </section>
        </div>

        <footer className="area-modal__footer">
          <button
            type="button"
            className="area-modal__submit area-modal__submit--secondary"
            onClick={onClose}
          >
            Закрыть
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AreaPlatesModal;
